'use client'

import { useState } from 'react'
import useSWR from 'swr'
import { Todo } from '@/lib/todo/Todo'
import { className } from '@/components/common/styles'

async function fetcher<T = unknown>(
  url: RequestInfo,
  options = {},
): Promise<T> {
  const res = await fetch(url, options)
  if (!res.ok) {
    throw new Error('Failed to fetch')
  }
  return res.json() as Promise<T>
}

const stateMutating = (
  <span className="info">Showing optimistic data, mutating...</span>
)
const stateSuccess = (
  <span className="success">
    Successfully mutated the resource and populated cache. Revalidating...
  </span>
)
const stateRevalidated = <span className="info">Revalidated the resource.</span>
const stateError = (
  <span className="error">
    Failed to mutate. Rolled back to previous state and revalidated the
    resource.
  </span>
)
const stateInit = <span className="info">&nbsp;</span>

export default function OptimisticUI() {
  const [text, setText] = useState('')
  const [state, setState] = useState(stateInit)
  const { data, mutate, error } = useSWR<Todo[], Error>('/api/todos', fetcher)
  const [disabled, setDisabled] = useState(false)

  const addTodo = () => {
    if (text === undefined || text === '' || state === stateMutating) {
      return
    }
    setDisabled(true)
    setState(stateMutating)
    const newTodo: Todo = { text }
    const payload = JSON.stringify(newTodo)

    try {
      // Update the local state immediately and fire the
      // request. Since the API will return the updated
      // data, there is no need to start a new revalidation
      // and we can directly populate the cache.
      const optimisticData = data ? [...data, newTodo] : [newTodo]

      // NOTE: Intentionally not awaited.
      void mutate(
        fetcher('/api/todos', {
          method: 'POST',
          body: payload,
        }),
        {
          optimisticData,
          rollbackOnError: true,
          populateCache: (newItem: Todo) => {
            setState(stateSuccess)
            return data ? [...data, newItem] : [newItem]
          },
          revalidate: true,
        },
      )
      setText('')
      setState(stateRevalidated)
      setDisabled(false)
    } catch (error) {
      // If the API errors, the original data will be
      // rolled back by swr automatically.
      console.log('mutate(): error:', error)
      setState(stateError)
    }
  }

  return (
    <div>
      <h1>Optimistic UI with swr</h1>
      {error ? `error= ${error.message}` : 'no errors'}
      <form onSubmit={(e) => e.preventDefault()}>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add your to-do here..."
          autoFocus
          disabled={disabled}
          className="rounded-xs border border-gray-400 dark:border-gray-600 bg-gray-100 dark:bg-gray-900 text-black dark:text-white"
        />
        <button className={className} type="submit" onClick={addTodo}>
          Add
        </button>
      </form>
      {state}
      {data && data.length ? (
        <ul>
          {data.map((todo) => {
            let id: number | undefined | string = todo.id
            if (id === undefined) {
              id = 'processing'
            }
            return (
              <li key={todo.id}>
                {todo.text} - {id}
              </li>
            )
          })}
        </ul>
      ) : (
        <i>
          No todos yet. Try adding lowercased &quot;banana&quot; and
          &quot;apple&quot; to the list.
        </i>
      )}
    </div>
  )
}
